"use client"

// src/components/TablaGastosComponent.js
import { useState, useEffect, useContext } from "react"
import { DataGrid } from "@mui/x-data-grid"
import {
  Autocomplete,
  TextField,
  InputAdornment,
  Modal,
  Box,
  Typography,
  Button,
  alpha,
  Paper,
  Chip,
  Tooltip,
  IconButton,
} from "@mui/material"
import PersonIcon from "@mui/icons-material/Person"
import CalendarTodayIcon from "@mui/icons-material/CalendarToday"
import AttachMoneyIcon from "@mui/icons-material/AttachMoney"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import ArrowForwardIcon from "@mui/icons-material/ArrowForward"
import SearchIcon from "@mui/icons-material/Search"

import { ErpContext } from "../../context/ErpContext"
import CustomPagination from "./CustomPagination"
import GraficasGastos from "./graficas/GraficasGastos"
import GestionGastosComponent from "./GestionGastosComponent"

import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import dayjs from "dayjs"

import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';

import { ThemeProvider, createTheme } from "@mui/material/styles"

// Reutilizo los estilos de la tabla de ventas (colores de header, hover, etc.)
import "./TablaVentasComponent.css"

const theme = createTheme({
  palette: {
    mode: "light",
    primary: { main: "#5b5b5f" },
    secondary: { main: "rgb(244, 177, 14)" },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: "none",
          boxShadow: "none",
          "&:hover": { boxShadow: "0 4px 8px rgba(0,0,0,0.1)" },
        },
      },
    },
    MuiPaper: {
      styleOverrides: { root: { borderRadius: 12 } },
    },
    MuiChip: {
      styleOverrides: { root: { fontWeight: 500 } },
    },
  },
})

export default function TablaGastosComponent() {
  const {
    subCategoriaTransaccion,
    getSubCategoriaTransaccion,
    entidadesClientesTresLetras,
    getEntidadesClientesTresLetras,
    getGastosTransacciones,
    putTransaccion,
    getEntidadByID,
    user,
    updateTransaccionPago,
    getCategoriaTransaccion,
    categoriaTransaccion,
    isNavbarCollapsed,
    updateDeleteTransaccion,
  } = useContext(ErpContext)

  // Catálogos
  const categorias = categoriaTransaccion
  const subCategories = subCategoriaTransaccion || []
  const entidades = entidadesClientesTresLetras || []

  // Estado tabla y filtros
  const today = dayjs()

  const [filtros, setFiltros] = useState({
    startDate: today.subtract(30, "day"),
    endDate: today,
    categoria: null,
    subcategoria: null,
    entidad: null,
  })
  const [searchTextEntidad, setSearchTextEntidad] = useState("")
  const [gastos, setGastos] = useState([])
  const [rowCount, setRowCount] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [loading, setLoading] = useState(false)
  const [totalPages, setTotalPages] = useState(0)

  // Estado modal y formulario
  const [openGraphModal, setOpenGraphModal] = useState(false)
  const [gestionModalState, setGestionModalState] = useState({
    open: false,
    mode: "create", // create | edit | duplicate
    initialData: null,
    actionRow: null,
    title: "Nuevo Gasto",
    onSubmit: null,
  })

  // Carga catálogos al montar
  useEffect(() => {
    getSubCategoriaTransaccion()
    getCategoriaTransaccion()
  }, [])

  // Búsqueda entidades filtro de tabla
  useEffect(() => {
    const fetchTablaEntidades = async () => {
      if (searchTextEntidad.length >= 3) {
        await getEntidadesClientesTresLetras(searchTextEntidad)
      }
    }
    fetchTablaEntidades()
  }, [searchTextEntidad])

  // Carga tabla
  const loadData = async () => {
    setLoading(true)
    try {
      const params = {
        page: page, // back 1-based
        limit: 100,
        categoria: filtros.categoria?.id || null,
        idSubCategoria: filtros.subcategoria?.id || null,
        idEntidad: filtros.entidad?.id || null,
        startDate: filtros.startDate ? filtros.startDate.startOf("day").toISOString() : null,
        endDate: filtros.endDate ? filtros.endDate.endOf("day").toISOString() : null,
      }

      const { data, totalCount, totalPages, currentPage } = await getGastosTransacciones(params)
      
      // Aplano descripciones para columnas visibles
      const filas = data.map((gasto) => {
        const fechaDate = new Date(gasto.fecha);
        return {
          ...gasto,
          fechaRaw: gasto.fecha,
          fechaTS: isNaN(fechaDate.getTime()) ? 0 : fechaDate.getTime(),
          fecha: new Intl.DateTimeFormat("es-AR", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          }).format(fechaDate),
          subCategoriaDesc: gasto.subCategoria?.descripcion ?? "",
          categoriaDesc: gasto.subCategoria?.categoria?.descripcion ?? "",
        };
      })

      // Orden default (descendente, más nuevas primero)
      filas.sort((a, b) => b.fechaTS - a.fechaTS)

      console.log("filas", filas)

      setGastos(filas)
      setRowCount(totalCount)
      setTotalPages(totalPages)
      setPage(currentPage) // mantener paginación como viene del back
    } catch (err) {
      console.error("Error al cargar gastos:", err)
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => {
    loadData()
  }, [])

  const handleFilterChange = (field, value) => {
    setFiltros((prev) => ({ ...prev, [field]: value }))
    setPage(0)
  }

  // Abre modal genérico
  const buscarFiltros = async () => {
    await loadData()
  }

  const buildInitialDataFromRow = async (row) => {
    const pago = row.transaccionPago?.[0]?.pago || {}
    const medioPago = pago.medioDePago || null
    const tipoMedioPago = medioPago?.tipoMedioDePago || null

    let entidadCompleta = null
    try {
      entidadCompleta = await getEntidadByID(row.idEntidad)
    } catch (err) {
      console.error("No se pudo obtener la entidad para pre-cargar edición:", err)
    }

    const entidadOption = entidadCompleta
      ? {
          label: `${entidadCompleta.descripcion} - ${entidadCompleta.apellido} - ${entidadCompleta.dniCuitCuil}`,
          descripcion: entidadCompleta.descripcion,
          apellido: entidadCompleta.apellido,
          dniCuitCuil: entidadCompleta.dniCuitCuil,
          id: entidadCompleta.id,
          idTipoEntidad: entidadCompleta.idTipoEntidad,
        }
      : row.entidad
      ? {
          label: `${row.entidad.descripcion} - ${row.entidad.apellido ?? ""} - ${row.entidad.dniCuitCuil ?? ""}`,
          descripcion: row.entidad.descripcion,
          apellido: row.entidad.apellido ?? "",
          dniCuitCuil: row.entidad.dniCuitCuil ?? "",
          id: row.idEntidad,
        }
      : null

    return {
      tipoEntidad: entidadCompleta?.idTipoEntidad || entidadOption?.idTipoEntidad || null,
      entidad: entidadOption,
      ubicacion: row.idUbicacion ? { id: row.idUbicacion } : null,
      moneda: 1,
      subcategoria: row.idCategorizacion,
      tipoMedioPago,
      medioPago,
      monto: row.montoTotal,
      descripcion: row.descripcion,
      aplicarCuentaCorriente: !!row.aplicarCuentaCorriente,
      fecha: row.fechaRaw || row.fechaTS || null,
    }
  }

  const handleEdit = async (row) => {
    const initialData = await buildInitialDataFromRow(row)
    setGestionModalState({
      open: true,
      mode: "edit",
      initialData,
      actionRow: row,
      title: "Editar Gasto",
      onSubmit: null, // se asigna más abajo en handleGestionSubmit
    })
  }

  const handleDuplicate = async (row) => {
    const initialData = await buildInitialDataFromRow(row)
    setGestionModalState({
      open: true,
      mode: "duplicate",
      initialData,
      actionRow: null,
      title: "Duplicar Gasto",
      onSubmit: null,
    })
  }

  const handleDelete = async (row) => {
    await updateDeleteTransaccion(row.id)
    await loadData()
  }

  const closeGestionModal = () =>
    setGestionModalState((prev) => ({
      ...prev,
      open: false,
      initialData: null,
      actionRow: null,
      onSubmit: null,
      mode: "create",
      title: "Nuevo Gasto",
    }))

  const handleGestionSubmit = async ({ formData }) => {
    if (gestionModalState.mode !== "edit" || !gestionModalState.actionRow) return
    const { actionRow } = gestionModalState
    await putTransaccion(
      actionRow.id,
      formData.monto,
      5,
      formData.entidad.id,
      formData.subcategoria,
      formData.descripcion,
      user.id,
      formData.ubicacion?.id || 1,
      1,
      0,
      0,
    )
    await updateTransaccionPago(actionRow.id, formData.medioPago.id, formData.monto)
  }

  // ==== Columnas con UI replicada de TablaVentas (solo diseño) ====
  const columns = [
    {
      field: "id",
      headerName: "ID",
      flex: 0.3,
      minWidth: 70,
      headerAlign: "center",
      renderCell: (params) => (
        <Box sx={{ display: "flex", justifyContent: "flex-start", gap: 1 }}>
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      ),
    },
    {
      field: "fecha",
      headerName: "Fecha",
      flex: 1,
      minWidth: 200,
      headerAlign: "center",
      sortable: false, // <--- clave: no mostrar control de orden
      renderCell: (params) => (
        <Box sx={{ display: "flex", justifyContent: "flex-start", gap: 1 }}>
          <CalendarTodayIcon fontSize="small" sx={{ color: alpha("#5b5b5f", 0.7) }} />
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      ),
    },
    {
      field: "montoTotal",
      headerName: "Monto",
      type: "number",
      flex: 0.8,
      minWidth: 130,
      headerAlign: "center",
      renderCell: (params) => (
        <Chip
          icon={<AttachMoneyIcon fontSize="small" />}
          label={params.value?.toLocaleString("es-AR") || "0.00"}
          size="small"
          sx={{
            fontWeight: "bold",
            bgcolor: alpha("#4CAF50", 0.1), // mismo chip/estilo que ventas
            color: "#4CAF50",
            "& .MuiChip-icon": { color: "#4CAF50" },
            display: "flex",
          }}
        />
      ),
    },
    {
      field: "entidad",
      headerName: "Entidad",
      flex: 1.2,
      minWidth: 180,
      headerAlign: "center",
      renderCell: (params) => {
        const label = params.row.entidad?.descripcion || ""
        return (
          <Tooltip title={label}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <PersonIcon fontSize="small" sx={{ color: alpha("#5b5b5f", 0.7) }} />
              <Typography variant="body2" noWrap fontWeight={500}>
                {label}
              </Typography>
            </Box>
          </Tooltip>
        )
      },
    },
    {
      field: "descripcion",
      headerName: "Descripción",
      flex: 1.6,
      minWidth: 260,
      headerAlign: "center",
      renderCell: (params) => (
        <Box sx={{ width: "100%", display: "flex", alignItems: "flex-start", justifyContent: "flex-start", boxSizing: "border-box", padding: "10px"}}>
          <Typography
            variant="body1"        // un toque más grande que body2
            fontWeight={600}       // resalta un poco más
            sx={{
              display: "-webkit-box",
              WebkitLineClamp: 2,  // hasta 2 líneas
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "normal",
              lineHeight: 1,
              color: "rgba(0,0,0,0.85)", // mejora el contraste sin cambiar la paleta
            }}
            title={params.value || ""} // tooltip nativo del browser
          >
            {params.value || ""}
          </Typography>
        </Box>
      ),
    },
    { field: "categoriaDesc", headerName: "Categoría", flex: 0.9, minWidth: 180, headerAlign: "center" },
    { field: "subCategoriaDesc", headerName: "Subcategoría", flex: 0.9, minWidth: 180, headerAlign: "center" },
    {
      field: "actions",
      headerName: "Acciones",
      flex: 0.5,
      minWidth: 150,
      headerAlign: "center",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <Tooltip title="Editar gasto">
              <IconButton
                className="colorGeneralParaIconos"
                onClick={() => handleEdit(row)}
                size="small"
                sx={{
                  bgcolor: alpha("#5b5b5f", 0.1),
                  "&:hover": { bgcolor: alpha("#5b5b5f", 0.2) },
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Duplicar gasto">
              <IconButton
                className="colorGeneralParaIconos"
                onClick={() => handleDuplicate(row)}
                size="small"
                sx={{
                  bgcolor: alpha("#5b5b5f", 0.1),
                  "&:hover": { bgcolor: alpha("#5b5b5f", 0.2) },
                }}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Eliminar gasto">
              <IconButton
                className="colorGeneralParaIconos"
                onClick={() => handleDelete(row)}
                size="small"
                sx={{
                  bgcolor: alpha("#5b5b5f", 0.1),
                  "&:hover": { bgcolor: alpha("#5b5b5f", 0.2) },
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
        </Box>
      ),
    },
  ]

  return (
    <ThemeProvider theme={theme}>
      {/* Filtros */}
      <Paper
        className="diseñoFiltroTransaccionesVenta"
        elevation={3}
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 2,
          backgroundColor: alpha(theme.palette.background.paper, 0.9),
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton
                onClick={() => {
                  if (!filtros.startDate) return
                  handleFilterChange("startDate", filtros.startDate.subtract(1, "day"))
                }}
                color="primary"
                size="small"
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>

              <DatePicker
                label="Desde"
                value={filtros.startDate}
                onChange={(newValue) => handleFilterChange("startDate", newValue)}
                format="DD/MM/YYYY"
                slotProps={{ textField: { size: "small", inputProps: { maxLength: 10 } } }}
                sx={{ width: 150 }}
              />

              <DatePicker
                label="Hasta"
                value={filtros.endDate}
                onChange={(newValue) => handleFilterChange("endDate", newValue)}
                format="DD/MM/YYYY"
                slotProps={{ textField: { size: "small", inputProps: { maxLength: 10 } } }}
                sx={{ width: 150 }}
              />

              <IconButton
                onClick={() => {
                  if (!filtros.startDate) return
                  handleFilterChange("startDate", filtros.startDate.add(1, "day"))
                }}
                color="primary"
                size="small"
              >
                <ArrowForwardIcon fontSize="small" />
              </IconButton>
            </Box>
          </LocalizationProvider>

          <Autocomplete
            size="small"
            options={categorias}
            getOptionLabel={(opt) => opt.descripcion || ""}
            isOptionEqualToValue={(opt, val) => opt.id === val?.id}
            value={filtros.categoria}
            onChange={(_, v) => handleFilterChange("categoria", v)}
            renderInput={(params) => <TextField {...params} label="Categoria" size="small" />}
            sx={{ width: "15%" }}
          />

          <Autocomplete
            size="small"
            options={subCategories}
            getOptionLabel={(opt) => opt.descripcion || ""}
            isOptionEqualToValue={(opt, val) => opt.id === val?.id}
            value={filtros.subcategoria}
            onChange={(_, v) => handleFilterChange("subcategoria", v)}
            renderInput={(params) => <TextField {...params} label="Subcategoría" size="small" />}
            sx={{ width: "15%" }}
          />

          <Autocomplete
            size="small"
            options={entidades.map((it) => ({
              ...it,
              label: `${it.descripcion} - ${it.apellido} - ${it.dniCuitCuil}`,
            }))}
            getOptionLabel={(opt) => opt.label}
            isOptionEqualToValue={(opt, val) => opt.id === val?.id}
            value={filtros.entidad}
            onChange={(_, v) => handleFilterChange("entidad", v)}
            onInputChange={(_, txt) => setSearchTextEntidad(txt)}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Buscar Entidad..."
                size="small"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <InputAdornment position="start">
                        <PersonIcon fontSize="small" />
                      </InputAdornment>
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }}
              />
            )}
            sx={{ width: "15%" }}
          />

          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              onClick={() =>
                setGestionModalState({
                  open: true,
                  mode: "create",
                  initialData: null,
                  actionRow: null,
                  title: "Nuevo Gasto",
                  onSubmit: null,
                })
              }
              sx={{
                backgroundColor: "rgb(244, 177, 14)",
                textTransform: "none",
                padding: "8px 16px",
                color: "white",
                "&:hover": { backgroundColor: alpha("rgb(244, 177, 14)", 0.8) },
              }}
            >
              Nuevo Gasto
            </Button>

            <Button
              variant="contained"
              size="small"
              onClick={buscarFiltros}
              startIcon={<SearchIcon />}
              sx={{
                backgroundColor: "#5b5b5f",
                textTransform: "none",
                padding: "8px 16px",
                color: "white",
                "&:hover": { backgroundColor: alpha("rgb(244, 177, 14)", 0.8) },
              }}
            >
              Buscar
            </Button>

            <Button
              variant="contained"
              size="small"
              onClick={() => setOpenGraphModal(true)}
              sx={{
                backgroundColor: theme.palette.secondary.main,
                textTransform: "none",
                padding: "8px 16px",
                color: "white",
                "&:hover": { backgroundColor: alpha(theme.palette.secondary.main, 0.8) },
              }}
            >
              Gráficas
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Tabla */}
      <Paper
        sx={{
          flex: 1,
          m: 1,
          borderRadius: 3,
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
          position: "relative",
          height: 600,
        }}
      >
        <DataGrid
          key={isNavbarCollapsed ? "collapsed" : "expanded"}
          rows={gastos}
          className="tablaVentasDataGrid"
          columns={columns}
          getRowId={(row) => row.id}
          rowCount={totalPages * 100}
          pagination
          paginationMode="server"
          getRowHeight={() => 35}
          headerHeight={35}
          sx={{
            width: "100%",
            border: 0,
            "& .MuiDataGrid-footerContainer": { display: "none !important" },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#5b5b5f",
              color: "white",
              borderBottom: "2px solid #333",
              height: "45px !important",
              minHeight: "45px !important",
              maxHeight: "45px !important",
            },
            "& .MuiDataGrid-columnHeader": {
              height: "45px !important",
              minHeight: "45px !important",
              maxHeight: "45px !important",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              color: "white !important",
              lineHeight: "45px !important",
            },
            "& .MuiDataGrid-columnHeaderTitle .MuiDataGrid-menuIconButton": {
              color: "white !important",
            },
            "& .MuiDataGrid-menuIconButton": {
              color: "rgb(244, 177, 14) !important",
            },
            "& .MuiInputBase-root": {
              color: "rgb(244, 177, 14) !important",
            },
            "& .MuiTablePagination-root": {
              color: "black !important",
            },
            "& .MuiSelect-icon": {
              color: "rgb(244, 177, 14) !important",
            },
            "& .MuiDataGrid-cell": {
              borderBottom: "1px solid #ccc",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            },
            "& .MuiDataGrid-row": {
              "&:nth-of-type(even)": { backgroundColor: "#F7F7F7" },
              "&:hover": {
                backgroundColor: "#E3EAFD !important",
                transition: "background 0.3s ease",
              },
            },
            "& .row-devolucion": {
              backgroundColor: alpha("#9e9e9e !important", 0.05),
              "&:hover": { backgroundColor: alpha("#9e9e9e !important", 0.1) },
            },
            "& .MuiDataGrid-virtualScroller": { lineHeight: "none !important", height: "10px !imporant" },
            "& .MuiDataGrid-footerContainer": {
              position: "sticky",
              bottom: 0,
              backgroundColor: "#ffffff",
              borderTop: "1px solid #ddd",
              zIndex: 10,
            },
            "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": { width: "2px" },
            "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
              backgroundColor: "#888",
              borderRadius: "10px",
            },
          }}
          components={{ Pagination: () => null }}
        />
      </Paper>

      {/* Contenedor del footer (paginación) */}
      <Box sx={{ flexShrink: 0, p: 1 }}>
        <CustomPagination
          currentPage={page}
          totalPages={totalPages}
          totalCount={rowCount}
          montoTotalTransacciones={0}
          vieneDe={"tablaGastos"}
          onPageChange={(newPage) => setPage(newPage)}
        />
      </Box>

      {/* Modal de Gráficas */}
      <Modal open={openGraphModal} onClose={() => setOpenGraphModal(false)} aria-labelledby="graph-modal-title">
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "95%",
            maxWidth: 1200,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            maxHeight: "90vh",
            overflow: "auto",
          }}
        >
          <Typography id="graph-modal-title" variant="h6" gutterBottom>
            Análisis de Gastos
          </Typography>

          <GraficasGastos gastos={gastos} subCategories={subCategories} filtros={filtros} />

          <Box mt={3} display="flex" justifyContent="flex-end">
            <Button onClick={() => setOpenGraphModal(false)} variant="contained">
              Cerrar
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Modal de Gestionar/Editar/Duplicar Gastos */}
      <Modal
        open={gestionModalState.open}
        onClose={closeGestionModal}
        aria-labelledby="gestion-gastos-modal-title"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "98%",
            maxWidth: 1400,
            minHeight: "70vh",
            maxHeight: "95vh",
            bgcolor: "background.paper",
            boxShadow: 24,
            borderRadius: 2,
            overflow: "auto",
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "#f1f1f1",
              borderRadius: "10px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#888",
              borderRadius: "10px",
              "&:hover": {
                backgroundColor: "#555",
              },
            },
          }}
        >
          <Box sx={{ p: 2 }}>
            <GestionGastosComponent
              mode={gestionModalState.mode}
              initialData={gestionModalState.initialData}
              titleOverride={gestionModalState.title}
              onClose={closeGestionModal}
              onSuccess={async () => {
                closeGestionModal()
                await loadData()
              }}
              onSubmit={gestionModalState.mode === "edit" ? handleGestionSubmit : null}
            />
          </Box>
        </Box>
      </Modal>
    </ThemeProvider>
  )
}
